import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import API_BASE_URL from '../config';

const FAVORITES_KEY = 'poupix_favorites';
const load = () => { try { return JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]'); } catch (_) { return []; } };

const FavoritesContext = createContext(null);

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used within FavoritesProvider');
  return ctx;
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(load);
  const { isAuthenticated, authHeaders } = useAuth();

  // Sync favourites to the backend so the price-refresh prioritiser knows
  // which products users care about.
  useEffect(() => {
    if (!isAuthenticated) return;
    const ids = favorites.map((f) => f.id);
    axios
      .post(`${API_BASE_URL}/favorites/sync`, { product_ids: ids }, { headers: authHeaders })
      .catch(() => {});
  }, [favorites, isAuthenticated, authHeaders]);

  const isFavorite = useCallback((id) => favorites.some((f) => f.id === Number(id)), [favorites]);

  const toggleFavorite = useCallback((product, priceInfo) => {
    setFavorites((prev) => {
      const numId = Number(product.id);
      const exists = prev.some((f) => f.id === numId);
      const next = exists
        ? prev.filter((f) => f.id !== numId)
        : [{
            id: numId,
            name: product.name,
            image_url: product.image_url || null,
            category: product.category || null,
            best_price: priceInfo?.price ?? null,
            best_supermarket: priceInfo?.supermarket ?? null,
            price_when_favorited: priceInfo?.price ?? null,
            added_at: Date.now(),
          }, ...prev].slice(0, 50);
      try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);

  const updateFavoritePrice = useCallback((id, price, supermarket) => {
    setFavorites((prev) => {
      const numId = Number(id);
      if (!prev.some((f) => f.id === numId)) return prev;
      const next = prev.map((f) => f.id === numId ? { ...f, best_price: price, best_supermarket: supermarket } : f);
      try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);

  const value = useMemo(() => ({ favorites, isFavorite, toggleFavorite, updateFavoritePrice }), [favorites, isFavorite, toggleFavorite, updateFavoritePrice]);
  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};
